class SoundManager {
    private ctx: AudioContext | null = null;
    private enabled: boolean = true;
    private ambientOscillators: { osc: OscillatorNode; gain: GainNode; id: string }[] = [];
    private currentAmbient: string | null = null;

    constructor() {
        // AudioContext must be resumed on user interaction
        if (typeof window !== 'undefined') {
            window.addEventListener('mousedown', () => this.init(), { once: true });
            window.addEventListener('keydown', () => this.init(), { once: true });
        }
    }

    private init() {
        if (!this.ctx) {
            this.ctx = new (window.AudioContext || (window as any).webkitAudioContext)();
        }
        if (this.ctx.state === 'suspended') {
            this.ctx.resume();
        }
    }

    play(type: 'jump' | 'attack' | 'hit' | 'step') {
        if (!this.enabled || !this.ctx) return;
        this.init(); // Ensure init

        const t = this.ctx.currentTime;
        const osc = this.ctx.createOscillator();
        const gain = this.ctx.createGain();

        osc.connect(gain);
        gain.connect(this.ctx.destination);

        switch (type) {
            case 'jump':
                osc.type = 'sine';
                osc.frequency.setValueAtTime(300, t);
                osc.frequency.linearRampToValueAtTime(600, t + 0.2);
                gain.gain.setValueAtTime(0.3, t);
                gain.gain.exponentialRampToValueAtTime(0.01, t + 0.2);
                osc.start(t);
                osc.stop(t + 0.2);
                break;
            case 'attack':
                // Retro "SWOOSH" -> Noise is hard with just oscillator, use sliding triangle
                osc.type = 'triangle';
                osc.frequency.setValueAtTime(400, t);
                osc.frequency.exponentialRampToValueAtTime(100, t + 0.15);
                gain.gain.setValueAtTime(0.3, t);
                gain.gain.linearRampToValueAtTime(0, t + 0.15);
                osc.start(t);
                osc.stop(t + 0.15);
                break;
            case 'hit':
                osc.type = 'square';
                osc.frequency.setValueAtTime(150, t);
                osc.frequency.exponentialRampToValueAtTime(50, t + 0.1);
                gain.gain.setValueAtTime(0.3, t);
                gain.gain.linearRampToValueAtTime(0, t + 0.1);
                osc.start(t);
                osc.stop(t + 0.1);
                break;
            case 'step':
                osc.type = 'triangle';
                osc.frequency.setValueAtTime(100, t);
                gain.gain.setValueAtTime(0.05, t);
                gain.gain.exponentialRampToValueAtTime(0.01, t + 0.05);
                osc.start(t);
                osc.stop(t + 0.05);
                break;
        }
    }

    /**
     * Play forest ambiance - layered ambient tones with nature sounds
     * Includes: wind whispers, bird chirps, rustling leaves
     */
    playForestAmbiance() {
        if (!this.enabled || !this.ctx) return;
        if (this.currentAmbient === 'forest') return; // Already playing
        
        this.stopAmbiance();
        this.init();
        const t = this.ctx.currentTime;

        // Layer 1: Deep wind base (very low frequency drone)
        const osc1 = this.ctx.createOscillator();
        const gain1 = this.ctx.createGain();
        const lfo1 = this.ctx.createOscillator(); // LFO for wobble
        const lfo1Gain = this.ctx.createGain();

        osc1.type = 'sine';
        osc1.frequency.setValueAtTime(35, t);
        
        lfo1.type = 'sine';
        lfo1.frequency.setValueAtTime(0.3, t); // Slow wobble
        lfo1Gain.gain.setValueAtTime(15, t); // ±15 Hz variation

        lfo1.connect(lfo1Gain);
        lfo1Gain.connect(osc1.frequency);
        
        gain1.gain.setValueAtTime(0.08, t);
        osc1.connect(gain1);
        gain1.connect(this.ctx.destination);
        
        osc1.start(t);
        lfo1.start(t);
        this.ambientOscillators.push({ osc: osc1, gain: gain1, id: 'wind-base' });
        this.ambientOscillators.push({ osc: lfo1, gain: lfo1Gain, id: 'lfo1' });

        // Layer 2: Bird chirp sequence (sparse)
        const spawnBirdChirp = () => {
            if (!this.ctx || this.currentAmbient !== 'forest') return;
            
            const chirpTime = this.ctx.currentTime;
            const osc = this.ctx.createOscillator();
            const gain = this.ctx.createGain();
            
            osc.type = 'sine';
            // Bird chirp: start high, slide down
            const startFreq = 1200 + Math.random() * 800; // 1200-2000 Hz
            const endFreq = 400 + Math.random() * 400; // 400-800 Hz
            const duration = 0.15 + Math.random() * 0.15; // 0.15-0.3s
            
            osc.frequency.setValueAtTime(startFreq, chirpTime);
            osc.frequency.exponentialRampToValueAtTime(endFreq, chirpTime + duration);
            
            gain.gain.setValueAtTime(0.05, chirpTime);
            gain.gain.exponentialRampToValueAtTime(0.01, chirpTime + duration);
            
            osc.connect(gain);
            gain.connect(this.ctx.destination);
            osc.start(chirpTime);
            osc.stop(chirpTime + duration);
            
            // Schedule next bird chirp (every 2-5 seconds)
            if (this.currentAmbient === 'forest') {
                setTimeout(() => spawnBirdChirp(), (2000 + Math.random() * 3000));
            }
        };
        
        // First bird chirp after 1-2 seconds
        setTimeout(() => spawnBirdChirp(), 1000 + Math.random() * 1000);

        // Layer 3: Rustling leaves (triangle wave noise-like)
        const spawnRustle = () => {
            if (!this.ctx || this.currentAmbient !== 'forest') return;
            
            const rustleTime = this.ctx.currentTime;
            const osc = this.ctx.createOscillator();
            const gain = this.ctx.createGain();
            
            osc.type = 'triangle';
            osc.frequency.setValueAtTime(200 + Math.random() * 400, rustleTime); // 200-600 Hz random
            
            gain.gain.setValueAtTime(0.02, rustleTime);
            gain.gain.exponentialRampToValueAtTime(0.001, rustleTime + 0.5);
            
            osc.connect(gain);
            gain.connect(this.ctx.destination);
            osc.start(rustleTime);
            osc.stop(rustleTime + 0.5);
            
            // Schedule next rustle (every 1-3 seconds)
            if (this.currentAmbient === 'forest') {
                setTimeout(() => spawnRustle(), (1000 + Math.random() * 2000));
            }
        };
        
        // First rustle after 0.5-1.5 seconds
        setTimeout(() => spawnRustle(), 500 + Math.random() * 1000);

        this.currentAmbient = 'forest';
    }

    /**
     * Stop all ambient sounds
     */
    stopAmbiance() {
        if (!this.ctx) return;
        const t = this.ctx.currentTime;

        this.ambientOscillators.forEach(({ osc, gain }) => {
            gain.gain.setValueAtTime(gain.gain.value, t);
            gain.gain.exponentialRampToValueAtTime(0.001, t + 0.5);
            
            try {
                osc.stop(t + 0.5);
            } catch {
                // Already stopped
            }
        });

        this.ambientOscillators = [];
        this.currentAmbient = null;
    }

    /**
     * Stop all sounds (useful when changing scenes)
     */
    stopAll() {
        this.stopAmbiance();
    }
}

export const sfx = new SoundManager();
